export const getTotalPriceDiscountByArticle = (quantity, price, discount) => {
  const total_price = quantity * price;
  const total = total_price - total_price * (discount / 100)
  return total > 0 ? total.toFixed(2) : 0;
};

/**
 * Calcula el precio base (sin IVA) de un producto
 * @param {number} price - Precio con IVA
 * @param {number} iva - Porcentaje de IVA
 * @returns {number} Precio base
 */
const calculateBasePrice = (price, iva) => {
  const pvp = Number(parseFloat(isNaN(price) ? 0.0 : price).toFixed(4));
  return Number(((pvp * 100) / (100 + iva)).toFixed(4));
};

/**
 * Calcula el IVA de un producto
 * @param {number} price - Precio con IVA
 * @param {number} basePrice - Precio base
 * @returns {number} IVA
 */
const calculateIva = (price, basePrice) => {
  const pvp = Number(parseFloat(isNaN(price) ? 0.0 : price).toFixed(4));
  return Number((pvp - basePrice).toFixed(4));
};

/**
 * Calcula los totales de la orden de la misma manera que en el carrito
 * @param {Array} items - Items de la orden
 * @param {number} discount - Porcentaje de descuento
 * @param {number} shippingCost - Costo de envío
 * @returns {Object} Objeto con subtotal_order, iva_order, total_order
 */
const calculateOrderTotals = (items, discount = 0, shippingCost = 0) => {
  let subtotal = 0;
  let iva = 0;

  // Calcular totales de productos (igual que en el carrito)
  items.forEach(item => {
    const price = parseFloat(item.price) || 0;
    const quantity = parseInt(item.quantity) || 0;
    const itemIva = item.product?.iva || 4; // IVA del producto, por defecto 4%
    
    const basePrice = calculateBasePrice(price, itemIva);
    const itemIvaAmount = calculateIva(price, basePrice);
    
    subtotal += basePrice * quantity;
    iva += itemIvaAmount * quantity;
  });

  // Aplicar descuento sobre (subtotal + iva)
  const totalBeforeDiscount = subtotal + iva;
  const discountAmount = totalBeforeDiscount * (discount / 100);
  const totalAfterDiscount = totalBeforeDiscount - discountAmount;

  // Calcular subtotal y iva después del descuento (proporcionalmente)
  const discountRatio = discount > 0 ? (totalAfterDiscount / totalBeforeDiscount) : 1;
  const finalSubtotal = subtotal * discountRatio;
  const finalIva = iva * discountRatio;
  const finalTotal = finalSubtotal + finalIva + shippingCost;

  return {
    subtotal_order: Number(finalSubtotal.toFixed(2)),
    iva_order: Number(finalIva.toFixed(2)),
    total_order: Number(finalTotal.toFixed(2))
  };
};

export const getTotalOrder = (articles, discount = 0, shippingCost = 0) => {
  if (!articles || articles.length === 0) {
    return (shippingCost || 0).toFixed(2);
  }

  // Normalizar artículos para usar el cálculo preciso (igual que en el carrito)
  const normalizedArticles = articles.map(item => {
    // Si ya tiene la estructura correcta (product y price), usarla directamente
    if (item.product && item.price !== undefined) {
      return {
        product: item.product,
        price: parseFloat(item.price) || 0,
        quantity: parseInt(item.quantity) || 1
      };
    }
    
    // Si tiene total_price, calcular el price unitario
    if (item.total_price !== undefined) {
      const quantity = parseInt(item.quantity) || 1;
      const totalPrice = parseFloat(item.total_price) || 0;
      const unitPrice = quantity > 0 ? totalPrice / quantity : 0;
      
      return {
        product: item.product || { iva: 4 },
        price: unitPrice,
        quantity: quantity
      };
    }
    
    // Fallback: estructura mínima
    return {
      product: item.product || { iva: 4 },
      price: parseFloat(item.price) || 0,
      quantity: parseInt(item.quantity) || 1
    };
  });

  const totals = calculateOrderTotals(normalizedArticles, discount, shippingCost);
  return totals.total_order.toFixed(2);
};

export const getSubTotalOrder = (articles, discount = 0) => {
  if (!articles || articles.length === 0) {
    return '0.00';
  }

  // Normalizar artículos para usar el cálculo preciso (igual que en el carrito)
  const normalizedArticles = articles.map(item => {
    // Si ya tiene la estructura correcta (product y price), usarla directamente
    if (item.product && item.price !== undefined) {
      return {
        product: item.product,
        price: parseFloat(item.price) || 0,
        quantity: parseInt(item.quantity) || 1
      };
    }
    
    // Si tiene total_price, calcular el price unitario
    if (item.total_price !== undefined) {
      const quantity = parseInt(item.quantity) || 1;
      const totalPrice = parseFloat(item.total_price) || 0;
      const unitPrice = quantity > 0 ? totalPrice / quantity : 0;
      
      return {
        product: item.product || { iva: 4 },
        price: unitPrice,
        quantity: quantity
      };
    }
    
    // Fallback: estructura mínima
    return {
      product: item.product || { iva: 4 },
      price: parseFloat(item.price) || 0,
      quantity: parseInt(item.quantity) || 1
    };
  });

  const totals = calculateOrderTotals(normalizedArticles, discount, 0);
  return totals.subtotal_order.toFixed(2);
};

/**
 * Calcula el IVA de la orden de la misma manera que en el carrito
 * @param {Array} items - Items de la orden
 * @param {number} discount - Porcentaje de descuento
 * @returns {number} IVA de la orden
 */
export const getIvaOrder = (items, discount = 0) => {
  if (!items || items.length === 0) return '0.00';

  // Normalizar artículos para usar el cálculo preciso (igual que en el carrito)
  const normalizedArticles = items.map(item => {
    // Si ya tiene la estructura correcta (product y price), usarla directamente
    if (item.product && item.price !== undefined) {
      return {
        product: item.product,
        price: parseFloat(item.price) || 0,
        quantity: parseInt(item.quantity) || 1
      };
    }
    
    // Si tiene total_price, calcular el price unitario
    if (item.total_price !== undefined) {
      const quantity = parseInt(item.quantity) || 1;
      const totalPrice = parseFloat(item.total_price) || 0;
      const unitPrice = quantity > 0 ? totalPrice / quantity : 0;
      
      return {
        product: item.product || { iva: 4 },
        price: unitPrice,
        quantity: quantity
      };
    }
    
    // Fallback: estructura mínima
    return {
      product: item.product || { iva: 4 },
      price: parseFloat(item.price) || 0,
      quantity: parseInt(item.quantity) || 1
    };
  });

  const totals = calculateOrderTotals(normalizedArticles, discount, 0);
  return totals.iva_order.toFixed(2);
};

export const getMinYear = (data) => {
  return data.reduce((min_year, item) => {
    const year = new Date(item.fecha).getFullYear();
    return year < min_year ? year : min_year;
  }, new Date(data[0].fecha).getFullYear());
};
